'use client';

import { type Context, createContext, useContext } from 'react';
import type { BuilderInterfaceStoreSelectors } from '../builder/selectors';
import type { InterfaceStoreManager } from './InterfaceStoreManager';
import type { RunnerInterfaceStoreSelectors } from './selectors';
import type { RunnerInterfaceStoreStateInitialValue } from './types';

// Explicit Context<T> annotation needed: without it, TS inlines the inferred Context
// structural type into emitted .d.ts and hits TS4023 on transitively-referenced
// NavigationComponents (defs/src path not reachable from this package's declaration output).
type InterfaceStoreManagerContextValue =
  | InterfaceStoreManager<
      RunnerInterfaceStoreSelectors | BuilderInterfaceStoreSelectors,
      RunnerInterfaceStoreStateInitialValue
    >
  | undefined;

/** accepts runner or builder manager so both can provide the same context */
const InterfaceStoreManagerContext: Context<InterfaceStoreManagerContextValue> =
  createContext<InterfaceStoreManagerContextValue>(undefined);

export { InterfaceStoreManagerContext };

export function useInterfaceStoreManager(): InterfaceStoreManager<
  RunnerInterfaceStoreSelectors | BuilderInterfaceStoreSelectors,
  RunnerInterfaceStoreStateInitialValue
> {
  const context = useContext(InterfaceStoreManagerContext);
  if (!context) {
    throw new Error(
      'useInterfaceStoreManager must be used within a InterfaceStoreManagerContext.Provider',
    );
  }
  return context;
}

/**
 * Non-throwing variant of {@link useInterfaceStoreManager}.
 *
 * Returns the manager when an `InterfaceStoreManagerContext.Provider` is present in the
 * tree, or `undefined` when it is not. Use this in code paths that may run either inside
 * a runner/builder (manager available) OR inside a plain `InterfacePageStoreContext.Provider`
 * subtree (no manager). Callers can branch on the result and fall back to a directly
 * provided store.
 *
 * Prefer the throwing {@link useInterfaceStoreManager} whenever the manager is genuinely
 * required — the thrown error documents the invariant and fails fast.
 */
export function useOptionalInterfaceStoreManager():
  | InterfaceStoreManager<
      RunnerInterfaceStoreSelectors | BuilderInterfaceStoreSelectors,
      RunnerInterfaceStoreStateInitialValue
    >
  | undefined {
  return useContext(InterfaceStoreManagerContext);
}
